/* 
	Driver for GSS Sensors controller

 	Manufacturer: Scottish technology firm Gas Sensing Solutions (GSS)
 	Homepage: https://www.gassensing.co.uk/

*/

import {TextEncoder} from "encoding";
import Bottleneck from "bottleneck";
import Device from "../device.js";


const TIMEOUT = 500;

class Module extends Device {
  static constructor() {
    Module.MODE_COMMAND = 0;
    Module.MODE_STREAMING = 1;
    Module.MODE_POLLING = 2;
  }  
  constructor(serialPort) {    
    super(serialPort);
    
    this.id = "uninitialized";
    this.status = "uninitialized";        
    this.__serialPort = serialPort;

    this.limiter = new Bottleneck({
      maxConcurrent: 1,
      minTime: 10
    });    

    /* publicate remote methods to clients */
    jfermi.RM_gssco2_calibratenitrogen = this.RM_gssco2_calibratenitrogen.bind(this);
    jfermi.RM_gssco2_calibratefreshair = this.RM_gssco2_calibratefreshair.bind(this);
    jfermi.RM_gssco2_calibrateknowngas = this.RM_gssco2_calibrateknowngas.bind(this);
  }
  async initialize() {
    try {            
      await this.__serialPort.open({
        baudRate: 9600,
        dataBits: 8,
        parity: "none",
        stopBits: 1    
      });      
      this.status = "OK";

      await this.setOperatingMode(Module.MODE_POLLING);

      this.id = await this.getDeviceInformation();      

      this.usbProductName = (await this.__serialPort.getInfo()).usbProductName;
	  this.usbSerialNumber = (await this.__serialPort.getInfo()).usbSerialNumber;    
      this.name = "gssco2";

    } catch(e) {
      this.status = "Failed";
      throw e;
    }
  }
  async update() {
    try {                    
      this.value = await this.getFilteredGasConcentration();
      this.temperature = await this.getTemperature();
      this.relativeHumidity = await this.getRelativeHumidity();
      this.status = "Ok";
    } catch(e) {
      this.status = "Failed";
      this.value = "-";
      throw e;
    }      
  }
  async __flush() {
    var reader = this.__serialPort.readable.getReader();

    /* flush queue */
    try {      
      setTimeout(function() {
        reader.releaseLock();
      }, 10);      
      while (true) {        
        const chunk = await reader.read();     
        if (Module.logLevel>2) console.log(`flushing ${chunk.value.byteLength} bytes(s)`);
      }
    } catch(e) {
      if (!(e instanceof TypeError))
        throw e;
    }    
  }  
  /* sending new command to the device while waiting for the response may cause concurrency problems           */
  /* so we wrapped the function and limited the maximum number of concurrent calls to 1 with bottlenck library */  
  async command(command) {
    return await this.limiter.schedule(() => this.command_wrapped(command));
  }
  async command_wrapped(command) {
    await this.__flush();

    var reader;

    var promise = new Promise(async function(resolve, reject) {                 

      const encoder = new TextEncoder();
      const writer = this.__serialPort.writable.getWriter();
      await writer.write(encoder.encode(`${command}\r\n`).buffer);
      writer.releaseLock();    
      reader = this.__serialPort.readable.getReader();

      var result = "";

      try {
        while (true) {
          const { value, done } = await reader.read();      
          result += String.fromCharCode.apply(null, new Uint8Array(value));      
          
          if (done) {
            // reader has been canceled.               
            reject( new Error("reader cancelled") );
            break;
          }           
          
          if (result.endsWith('\r\n')) {
            // |reader| has been canceled.       
            result = result.replace("\r\n", "");
            result = result.replace(" ", "");
            result = result.replace(command, "");
            resolve(result);
            break;
          }        
        } //while
        reader.releaseLock();
      } catch(e) {
        if (e instanceof TypeError) {
          reject( new Error("timeout") );
        } else {  
          throw e;        
        }

      }
    }.bind(this));

    var promiseTimeout = new Promise(function(resolve, reject) {
      setTimeout(function() {        
        reader.releaseLock();        
        reject( new Error("timeout"));
      }, TIMEOUT);
    }.bind(this));

    return Promise.race([promise, promiseTimeout]);          

  }
  async getDeviceInformation() {
    return await this.command("Y");
  }
  async setOperatingMode(mode) {
    return await this.command(`K ${mode}`);
  }
  async setDigitalFilter(n) {
    return await this.command(`A ${n}`);
  }
  async getDigitalFilter() {
    return parseInt( await this.command("a") );
  }
  async getFilteredGasConcentration() {
    var multiplier = await this.command(".");
    var co2 = await this.command("Z");
    return multiplier * co2;
  }
  async getTemperature() {
    var t = await this.command("T");
    t = t-1000;
    t = t/10;
    return t;
  }
  async getRelativeHumidity() {
    var h = await this.command("H");
    return h/10;
  }
  async zeroCalibrateIn0ppm() {
    return await this.command("U");
  }
  /* Fresh Air meas 400ppm by default */
  async zeroCalibrateInFreshAir() {
    return await this.command("G");
  }
  async zeroCalibrateIn(ppm) {
    /*
      For example, if the user wants to zero the sensor in a known concentration of gas (e.g. 450ppm), the
      value written to the sensor must be 450/scaling factor.
    */
    var multiplier = await this.command(".");
    ppm /= multiplier;
    ppm = Math.round(ppm);
    return await this.command(`X ${ppm}`);
  }
  async RM_gssco2_calibratenitrogen(request) {
    try {
      var result = await this.zeroCalibrateIn0ppm();
      console.log(`${request.client.username}: GSS CO2 sensors' zero point has been calibrated.`);
    } catch(e) {
      console.error(`${request.client.username}: GSS CO2 sensor calibration failed: ${e}`);
      throw Error(e);
    }
    return true;
  }
  async RM_gssco2_calibratefreshair(request) {
    try {
      var result = await this.zeroCalibrateInFreshAir();
      console.log(`${request.client.username}: GSS CO2 sensors' zero point has been calibrated.`);
    } catch(e) {
      console.error(`${request.client.username}: GSS CO2 sensor calibration failed: ${e}`);
      throw Error(e);
    }
    return true;
  }
  async RM_gssco2_calibrateknowngas(request, ppm) {
    try {
      var result = await this.zeroCalibrateIn(ppm);
      console.log(`${request.client.username}: GSS CO2 sensors' zero point has been calibrated.`);
    } catch(e) {
      console.error(`${request.client.username}: GSS CO2 sensor calibration failed: ${e}`);
      throw Error(e);
    }
    return true;
  }  
  toJSON() {
    return({
      id: this.id,
      usbProductName: this.usbProductName,
      usbSerialNumber: this.usbSerialNumber,
      status: this.status,
      value: this.value,
      temperature: this.temperature,
      relativeHumidity: this.relativeHumidity
    });
  }
}

Module.constructor();

export default Module;